import { postBooking } from "../../../app/api/postBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { uuid } from "../../../core/utils/Uuid";
import { getSelectedProductOffersFromTrainType } from "../../../Lib/Utils/booking";
import { getRecoilStateValue, getRecoilStateValueWithType, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse, IBookingResponse, IOffer, OffersRequest, PassengerSpecification } from "../../../Orchestrator/Types/Index";
import { IBookingRequest } from "../../../Orchestrator/Types/Types/BookingTypes";
import { TrainType } from "../../../Orchestrator/Types/Types/TrainTypes";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";

type IPrebookMultiBookingActionResult = IBookingResponse | null;

class PrebookMultiBookingAction extends BaseAction<IPrebookMultiBookingActionResult, void> {
  getSelectedTrainOffer(train: TrainType): IOffer[] | null {
    let productOffer = getSelectedProductOffersFromTrainType(train) || null;
    return productOffer;
  }

  async processBooking(): Promise<{ booking: IBookingResponse } | null> {
    const multiBookingSelectedTrainsState = getRecoilStateValueWithType("multiBookingSelectedTrainsAtom")
    const multiBookingFormValuesAtom = getRecoilStateValueWithType("multiBookingFormValuesAtom");

    const offersRequest = getRecoilStateValue<OffersRequest>("offersRequestAtom");

    if (!offersRequest) {
      console.error('Offers request data is missing.');
      return null;
    }

    // Prepare the booking parameters        
    const passengerRefs: string[] = [];
    const passengerSpecifications: PassengerSpecification[] = [];

    offersRequest.anonymousPassengerSpecifications.forEach((element: any) => {
      passengerRefs.push(element.externalRef);
      passengerSpecifications.push({
        externalRef: element.externalRef,
        detail: null,
        type: element.type,
        age: element.age,
        cards: (element?.cards || []).length > 0 ? element.cards : [],
        ...(element.prmNeeds?.length > 0 ? { prmNeeds: element.prmNeeds } : {})
      });
    });

    const BookingRequestOffers: IBookingRequest["offers"] = [];
    multiBookingSelectedTrainsState?.map(trainOffer => {
      let outboundProductOffer = (trainOffer.outbound.train && trainOffer.outbound.selected) ? this.getSelectedTrainOffer(trainOffer.outbound.train) : null;
      let inboundProductOffer = (trainOffer.inbound?.train && trainOffer.inbound.selected) ? this.getSelectedTrainOffer(trainOffer.inbound.train) : null;

      // code to add shuttle offers
      // if (outboundProductOffer && outboundProductOffer?.length) {
      //   outboundProductOffer = outboundProductOffer.concat(trainOffer.outbound.train?.shuttleOffers || [])
      // }
      // if (inboundProductOffer && inboundProductOffer?.length) {
      //   inboundProductOffer = inboundProductOffer.concat(trainOffer.inbound?.train?.shuttleOffers || [])
      // }


      if (trainOffer.outbound.selected && outboundProductOffer) {
        outboundProductOffer.map(offer => {
          BookingRequestOffers.push({
            offerId: offer.offerId,
            passengerRefs: passengerRefs,
            throughTicketTags: ["MultiBooking"],
            ...(multiBookingFormValuesAtom?.seating?.position && {
              placeSelections: offer.tripCoverage.coveredLegIds.map(legId => ({
                reservationId: offer.offerId,
                accommodations: [
                  {
                    passengerRefs: passengerRefs,
                    accommodationType: "SEAT",
                    accommodationSubType: "ANY_SEAT",
                    placeProperties: multiBookingFormValuesAtom?.seating?.position ? [multiBookingFormValuesAtom.seating.position] : []
                  }
                ],
                tripLegCoverage: {
                  tripId: offer.tripCoverage.coveredTripId,
                  legId: legId
                }
              }))
            })
          })
        })
      }

      if (trainOffer.inbound?.selected && inboundProductOffer) {
        inboundProductOffer.map(offer => {
          BookingRequestOffers.push({
            offerId: offer.offerId,
            passengerRefs: passengerRefs,
            throughTicketTags: ["MultiBooking"],
            ...(multiBookingFormValuesAtom?.seating?.position && {
              placeSelections: offer.tripCoverage.coveredLegIds.map(legId => ({
                reservationId: offer.offerId,
                accommodations: [
                  {
                    passengerRefs: passengerRefs,
                    accommodationType: "SEAT",
                    accommodationSubType: "ANY_SEAT",
                    placeProperties: multiBookingFormValuesAtom?.seating?.position ? [multiBookingFormValuesAtom.seating.position] : []
                  }
                ],
                tripLegCoverage: {
                  tripId: offer.tripCoverage.coveredTripId,
                  legId: legId
                }
              }))
            })
          })
        })
      }
    })

    const bookingParams: IBookingRequest = {
      offers: BookingRequestOffers,
      passengerSpecifications: passengerSpecifications,
      externalRef: uuid(),
    };

    // Update the booking request state in Recoil
    updateRecoilState<IBookingRequest>('bookingRequestAtom', bookingParams);

    try {
      // Clear the booking response atom before making the request
      updateRecoilState('bookingResponseAtom', null);
      // Post booking data to the API
      const response: IApiResponse<IBookingResponse> = await postBooking(bookingParams);
      if (response.status === 'ok' && response.answer) {

        const bookingResponse: IBookingResponse = response.answer;

        updateRecoilState('bookingResponseAtom', bookingResponse);

        prepareBookingSummaryAction.setParams(bookingResponse.booking)
        await prepareBookingSummaryAction.execute()

        return {
          booking: bookingResponse
        }
      }
    } catch (error: any) {
      console.error('Error processing booking:', error);
      return null
    }
    // Ensure a return statement for all code paths
    return null;
  }

  override async run(): Promise<IPrebookMultiBookingActionResult> {
    let result: IPrebookMultiBookingActionResult = null;

    try {
      // TODO: check and add this atoms if needed so
      // updateRecoilState("outboundOfferIdAtom", offerId);
      // updateRecoilState("outboundShuttleOfferIdAtom", shuttleOfferId);
      // updateRecoilState("trainTypeAtom", TrainTypeEnum.inbound);
      // updateRecoilState("inboundOfferIdAtom", offerId);
      // updateRecoilState("inboundSearchStateAtom", searchParams);
      // updateRecoilState("passengerStateAtom", filteredBookingPassengers);
      updateRecoilState("passengerStateAtom", [])
      let processBookingData = await this.processBooking();

      updateRecoilState("passengerStateAtom", processBookingData?.booking?.booking?.passengers);

      result = processBookingData?.booking || null
    } catch (error) {
      console.error("error in pre-booking multi booking", error)
    } finally {
      return result
    }
  }
}

export const prebookMultiBookingAction = new PrebookMultiBookingAction();
